import React, { useState } from 'react';
import Navbar2 from '../navbar/Navbar2';

// Reusable ArrowDownIcon component
const ArrowDownIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M0.292308 4.86572C-0.097436 5.2706 -0.097436 5.92623 0.292308 6.33008L8.55587 14.8926C9.33636 15.7023 10.6025 15.7023 11.383 14.8926L19.7075 6.26807C20.0933 5.86732 20.0983 5.21989 19.7175 4.81397C19.3288 4.39977 18.6882 4.39475 18.2935 4.80274L10.6765 12.6963C10.2857 13.1012 9.65314 13.1012 9.2624 12.6963L1.70538 4.86572C1.31563 4.46083 0.683051 4.46083 0.292308 4.86572Z"
      fill="black"
    />
  </svg>
);

// Reusable InputField component
const InputField = ({ label, id, type = 'text', placeholder, required }) => (
  <div className="flex flex-col gap-2 w-full">
    <label htmlFor={id} className="text-gray-900 text-lg font-normal">
      {label} {required && <span className="text-red-500">*</span>}
    </label>
    <input
      type={type}
      id={id}
      placeholder={placeholder}
      required={required}
      className="h-12 px-4 rounded-md border border-neutral-200 text-lg text-gray-700 focus:outline-none focus:ring-2 focus:ring-green-600"
    />
  </div>
);

// Reusable SelectField component
const SelectField = ({ label, id, options, required }) => (
  <div className="flex flex-col gap-2 w-full">
    <label htmlFor={id} className="text-gray-900 text-lg font-normal">
      {label} {required && <span className="text-red-500">*</span>}
    </label>
    <div className="relative">
      <select
        id={id}
        required={required}
        className="h-12 px-4 rounded-md border border-neutral-200 text-lg text-gray-700 appearance-none w-full focus:outline-none focus:ring-2 focus:ring-green-600"
      >
        <option value="" disabled selected>
          Select {label.toLowerCase()}
        </option>
        {options.map((option) => (
          <option key={option} value={option}>
            {option}
          </option>
        ))}
      </select>
      <span className="absolute right-4 top-1/2 -translate-y-1/2">
        <ArrowDownIcon />
      </span>
    </div>
  </div>
);

// Reusable TextAreaField component
const TextAreaField = ({ label, id, placeholder, required }) => (
  <div className="flex flex-col gap-2 w-full">
    <label htmlFor={id} className="text-gray-900 text-lg font-normal">
      {label} {required && <span className="text-red-500">*</span>}
    </label>
    <textarea
      id={id}
      placeholder={placeholder}
      required={required}
      className="h-32 p-4 rounded-md border border-neutral-200 text-lg text-gray-700 focus:outline-none focus:ring-2 focus:ring-green-600 resize-none"
    />
  </div>
);

const MobileApplicationForm = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    position: '',
    experience: '',
    availability: '',
    qualifications: '',
    motivation: '',
    terms: false,
  });

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.terms) {
      alert('Please agree to the terms and conditions.');
      return;
    }
    console.log('Form submitted:', formData);
    // TODO: Implement API call for form submission
  };

  return (
    <section className="w-full bg-gray-50 py-12">
      
      <div className="max-w-md mx-auto flex flex-col items-center gap-8 px-6">
        <Navbar2 /> 
        {/* Headline */}
        <div className="flex flex-col items-center gap-4">
          <h2 className="text-center text-3xl font-bold text-gray-900">Apply Today</h2>
          <p className="text-center text-gray-500 text-xl font-normal leading-7 tracking-wide">
            Take the first step towards your rewarding career in healthcare
          </p>
        </div>

        {/* Form */}
        <form
          onSubmit={handleSubmit}
          className="w-full bg-white rounded-2xl border border-neutral-300 p-6 flex flex-col gap-8"
        >
          {/* Personal Information */}
          <div className="flex flex-col gap-6">
            <InputField
              label="First Name"
              id="firstName"
              name="firstName"
              placeholder="Enter your first name"
              required
              value={formData.firstName}
              onChange={handleChange}
            />
            <InputField
              label="Last Name"
              id="lastName"
              name="lastName"
              placeholder="Enter your last name"
              required
              value={formData.lastName}
              onChange={handleChange}
            />
            <InputField
              label="Email Address"
              id="email"
              name="email"
              type="email"
              placeholder="your.email@example.com"
              required
              value={formData.email}
              onChange={handleChange}
            />
            <InputField
              label="Phone Number"
              id="phone"
              name="phone"
              type="tel"
              placeholder="07123 456789"
              required
              value={formData.phone}
              onChange={handleChange}
            />
          </div>

          {/* Position, Experience, Availability */}
          <div className="flex flex-col gap-6">
            <SelectField
              label="Position"
              id="position"
              name="position"
              options={['Registered Nurse', 'Care Assistant', 'Senior Nurse', 'Other']}
              required
              value={formData.position}
              onChange={handleChange}
            />
            <SelectField
              label="Experience"
              id="experience"
              name="experience"
              options={['0-1 years', '1-3 years', '3-5 years', '5+ years']}
              required
              value={formData.experience}
              onChange={handleChange}
            />
            <SelectField
              label="Availability"
              id="availability"
              name="availability"
              options={['Full-time', 'Part-time', 'Flexible']}
              required
              value={formData.availability}
              onChange={handleChange}
            />
          </div>

          {/* Qualifications & Motivation */}
          <div className="flex flex-col gap-6">
            <TextAreaField
              label="Qualifications & Certifications"
              id="qualifications"
              name="qualifications"
              placeholder="Please list your relevant qualifications, certifications, and training..."
              required
              value={formData.qualifications}
              onChange={handleChange}
            />
            <TextAreaField
              label="Why do you want to join RightTime Medicals?"
              id="motivation"
              name="motivation"
              placeholder="Tell us about your motivation to work in healthcare and why you’d like to join our team..."
              required
              value={formData.motivation}
              onChange={handleChange}
            />
          </div>

          {/* Terms Checkbox */}
          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              id="terms"
              name="terms"
              checked={formData.terms}
              onChange={handleChange}
              className="w-5 h-5 border border-neutral-300 rounded focus:ring-green-600"
            />
            <label htmlFor="terms" className="text-neutral-700 text-lg font-medium">
              I agree to the{' '}
              <a href="/terms" className="text-green-600 hover:underline">
                terms and conditions
              </a>{' '}
              and{' '}
              <a href="/privacy" className="text-green-600 hover:underline">
                privacy policy
              </a>{' '}
              <span className="text-red-500">*</span>
            </label>
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            className="flex items-center justify-center gap-2 px-8 py-4 bg-green-600 text-white text-xl font-medium rounded-full hover:bg-green-700 transition-all duration-300"
          >
            Submit Application
            <svg width="16" height="15" viewBox="0 0 16 15" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
              <path
                d="M1.07691 8.07691H13.5298L7.59326 14.0163C7.48581 14.1244 7.4255 14.2706 7.4255 14.4231C7.4255 14.5755 7.48581 14.7217 7.59326 14.8298C7.70136 14.9372 7.84758 14.9976 7.99999 14.9976C8.15241 14.9976 8.29863 14.9372 8.40672 14.8298L15.3298 7.90672C15.3571 7.87968 15.3813 7.84969 15.4019 7.81729C15.4111 7.80818 15.4198 7.79854 15.4279 7.78845C15.4279 7.76825 15.4481 7.75095 15.4567 7.73075L15.474 7.67306C15.4757 7.65676 15.4757 7.64033 15.474 7.62402C15.4888 7.54975 15.4888 7.4733 15.474 7.39902C15.4757 7.38272 15.4757 7.36629 15.474 7.34999L15.4567 7.29229C15.4567 7.2721 15.4365 7.25479 15.4279 7.2346C15.4203 7.21956 15.4116 7.2051 15.4019 7.19133C15.3813 7.15893 15.3571 7.12894 15.3298 7.10191L8.40672 0.17883C8.355 0.118437 8.29136 0.0693874 8.21978 0.0347586C8.14821 0.0001297 8.07025 -0.019331 7.99079 -0.0223999C7.91134 -0.0254688 7.83211 -0.0120802 7.75808 0.0169239C7.68404 0.045929 7.6168 0.0899239 7.56058 0.146148C7.50436 0.202372 7.46036 0.26961 7.43136 0.343644C7.40235 0.417678 7.38896 0.496908 7.39203 0.576362C7.3951 0.655815 7.41456 0.733776 7.44919 0.805351C7.48382 0.876927 7.53287 0.940573 7.59326 0.992292L13.5298 6.92306H1.07691C0.923905 6.92306 0.777163 6.98384 0.668968 7.09204C0.560775 7.20023 0.499992 7.34698 0.499992 7.49999C0.499992 7.653 0.560775 7.79974 0.668968 7.90793C0.777163 8.01613 0.923905 8.07691 1.07691 8.07691Z"
                fill="white"
              />
            </svg>
          </button>
        </form>
      </div>
    </section>
  );
};

export default MobileApplicationForm;